import React, { Component } from 'react';
import { Eye, Star, Menu, Search, User, CheckCircle, Clock, ShieldCheck, ThumbsUp } from 'lucide-react';
// --- Reusable Components ---
const CTAButton = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => <a href="https://flashgizmo.com/products/pageglow%E2%84%A2" target="_blank" rel="noopener noreferrer" className={`inline-block w-full md:w-auto bg-[#28a745] hover:bg-[#218838] text-white font-bold py-4 px-8 rounded-md shadow-lg transform transition hover:-translate-y-0.5 text-lg md:text-xl uppercase tracking-wide text-center ${className}`} data-id="element-0">
    {children}
  </a>;
const TestimonialBox = ({
  quote,
  author,
  location,
  image
}: {
  quote: string;
  author: string;
  location: string;
  image?: string;
}) => <div className="bg-gray-50 border-l-4 border-[#28a745] p-6 my-8 rounded-r-lg" data-id="element-1">
    <div className="flex mb-2" data-id="element-2">
      {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" data-id="element-3" />)}
    </div>
    <p className="text-gray-700 italic text-lg mb-4 font-serif leading-relaxed" data-id="element-4">
      "{quote}"
    </p>
    <div className="flex items-center gap-3" data-id="element-5">
      {image && <img src={image} alt={author} className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm" data-id="element-6" />}
      <div className="text-sm font-bold text-gray-900 uppercase tracking-wider" data-id="element-7">
        — {author},{' '}
        <span className="text-gray-500 font-normal normal-case" data-id="element-8">
          {location}
        </span>
      </div>
    </div>
  </div>;
const SectionDivider = () => <div className="flex justify-center my-10" data-id="element-9">
    <div className="w-24 h-1 bg-gray-200 rounded-full" data-id="element-10"></div>
  </div>;
// --- Main Application ---
export function App() {
  return <div className="min-h-screen bg-gray-50" data-id="element-11">
      {/* Urgency Banner */}
      <div className="bg-[#fff8dc] border-b-2 border-[#f0ad4e] py-3 px-4" data-id="element-12">
        <div className="max-w-6xl mx-auto flex items-center justify-center gap-2 text-center" data-id="element-13">
          <span className="text-2xl" data-id="element-14">📢</span>
          <p className="text-sm md:text-base font-bold text-gray-900" data-id="element-15">
            READER ALERT: PageGlow Now Available at 32% OFF - Limited Stock at
            This Price | Free Shipping Included
          </p>
        </div>
      </div>

      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm" data-id="element-16">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between" data-id="element-17">
          <div className="flex items-center gap-4" data-id="element-18">
            <button className="p-1 hover:bg-white/10 rounded" data-id="element-19">
              <Menu className="w-6 h-6" data-id="element-20" />
            </button>
            <div className="font-serif text-xl md:text-2xl font-bold tracking-tight" data-id="element-21">
              Lifestyle Insights Journal
            </div>
          </div>
          <div className="flex items-center gap-4" data-id="element-22">
            <button className="p-1 hover:bg-white/10 rounded hidden md:block" data-id="element-23">
              <Search className="w-5 h-5" data-id="element-24" />
            </button>
            <button className="p-1 hover:bg-white/10 rounded hidden md:block" data-id="element-25">
              <User className="w-5 h-5" data-id="element-26" />
            </button>
            <button className="md:hidden bg-[#28a745] text-xs font-bold px-3 py-1.5 rounded uppercase" data-id="element-27">
              Subscribe
            </button>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="max-w-7xl mx-auto px-4 py-8 md:py-12" data-id="element-28">
        <div className="flex flex-col lg:flex-row gap-8" data-id="element-29">
          {/* Main Article Content */}
          <div className="flex-1 max-w-3xl" data-id="element-30">
            <article data-id="element-31">
              {/* Article Header */}
              <div className="mb-8 border-b border-gray-200 pb-8" data-id="element-32">
                <span className="inline-block bg-red-600 text-white text-xs font-bold px-2 py-1 uppercase tracking-wider mb-4 rounded-sm" data-id="element-33">
                  Trending
                </span>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-4 font-sans" data-id="element-34">
                  Why You've Been Forced To Choose Between Reading Comfort and
                  Partner's Sleep (And How To Get Both)
                </h1>

                <div className="flex flex-col md:flex-row md:items-center justify-between mt-6 text-sm text-gray-500 gap-4" data-id="element-35">
                  <div className="flex items-center gap-3" data-id="element-36">
                    <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden" data-id="element-37">
                      <img src="https://randomuser.me/api/portraits/women/68.jpg" alt="Sarah Mitchell" className="w-full h-full object-cover" data-id="element-38" />
                    </div>
                    <div data-id="element-39">
                      <p className="font-bold text-gray-900" data-id="element-40">
                        By Sarah Mitchell
                      </p>
                      <p data-id="element-41">Home & Lifestyle Writer</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-xs md:text-sm" data-id="element-42">
                    <span className="flex items-center gap-1" data-id="element-43">
                      <Clock className="w-4 h-4" data-id="element-44" /> Posted 2 hours ago
                    </span>
                    <span className="flex items-center gap-1 text-gray-700 font-medium" data-id="element-45">
                      <Eye className="w-4 h-4" data-id="element-46" /> 47,832 views
                    </span>
                  </div>
                </div>
              </div>

              {/* Hero Image */}
              <div className="mb-12" data-id="element-47">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/9nackabB9Z15nnUzXjZpjF/Untitled_design_%2810%29.png" alt="PageGlow reading light illuminating an open book in the dark" className="w-full rounded-lg shadow-2xl" data-id="element-48" />
              </div>

              {/* Article Body */}
              <div className="prose prose-lg max-w-none font-serif text-gray-800 leading-loose" data-id="element-49">
                <p className="first-letter:text-5xl first-letter:font-bold first-letter:float-left first-letter:mr-3 first-letter:mt-[-10px] first-letter:text-gray-900" data-id="element-50">
                  You already know this feeling.
                </p>

                <p data-id="element-51">
                  It's 10 PM. You're in bed with a book you've been excited to
                  read all day. Your partner is next to you, scrolling their
                  phone, getting ready to sleep.
                </p>

                <p data-id="element-52">
                  You turn on your reading light. Position it carefully. Try to
                  angle it just right so it doesn't bother them.
                </p>

                <p data-id="element-53">
                  Five minutes later, they shift. Pull the covers up higher.
                  Turn away from you.
                </p>

                <p className="font-bold" data-id="element-54">You notice. Of course you notice.</p>

                <p data-id="element-55">
                  And that small knot of guilt starts forming in your stomach.
                </p>

                <p data-id="element-56">
                  This is the impossible choice book lovers face every single
                  night: Read with enough light to actually see the pages... or
                  don't disturb your partner.
                </p>

                <p className="text-xl font-bold text-center my-8 text-gray-900 italic" data-id="element-57">
                  "You cannot have both. You have to pick one."
                </p>

                <p data-id="element-58">
                  And if you've tried clip-on lights, adjustable bedside lamps,
                  or even those neck lights everyone raves about, you already
                  know—none of them actually solve this problem.
                </p>

                <p data-id="element-59">They all just make the choice slightly less painful.</p>

                <h2 className="text-2xl md:text-3xl font-bold font-sans text-gray-900 mt-12 mb-6" data-id="element-60">
                  Every Solution You've Tried Works The Same Way (And That's The
                  Problem)
                </h2>

                <div className="my-8" data-id="element-61">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/5LYTavGQYpow8GDSCAXVz1/Untitled_%281200_x_675_px%29_%281200_x_800_px%29_%281200_x_800_px%29_%282%29.png" alt="Comparison of traditional clip-on reading light versus PageGlow panel light" className="w-full rounded-lg shadow-2xl" data-id="element-62" />
                </div>

                <p data-id="element-63">Let's talk about what you've probably already purchased.</p>

                <p data-id="element-64">
                  Maybe you started with a bedside lamp. But that lights up half
                  the room, doesn't it? Your partner. The wall. The ceiling.
                  Everything gets illuminated when all you wanted was to see
                  your book pages.
                </p>

                <p data-id="element-65">
                  So you upgraded to a clip-on book light with the flexible
                  neck. Better, right? You can aim it. Position it exactly on
                  the pages.
                </p>

                <p data-id="element-66">
                  Except... the light still spills. You've seen it. Even when
                  you spend five minutes adjusting that neck trying to minimize
                  the spill, there's still a glow hitting your partner's pillow.
                  Still enough light bleeding onto the wall behind you. Still
                  enough for them to notice.
                </p>

                <p data-id="element-67">
                  And those neck lights—the ones like Glocusent that everyone
                  seems to love? Those might actually be worse.
                </p>

                <p data-id="element-68">
                  Because the light source is attached to you. Every time you
                  shift position, adjust your pillow, or turn a page, you're
                  moving the light. It's like wearing a lighthouse around your
                  neck, sweeping the room every few minutes.
                </p>

                <p data-id="element-69">
                  Your partner isn't just dealing with ambient light. They're
                  dealing with a rotating beam.
                </p>

                <div className="bg-blue-50 p-6 rounded-lg my-8 border border-blue-100" data-id="element-70">
                  <p className="font-bold text-blue-900 mb-2 font-sans" data-id="element-71">
                    THE CORE PROBLEM:
                  </p>
                  <p className="text-blue-800 m-0" data-id="element-72">
                    Every light you've tried works the same way. They were
                    positioned somewhere away from your book, shooting light
                    toward it. And light traveling through air? It spreads
                    everywhere. Always.
                  </p>
                </div>

                <p data-id="element-73">
                  This isn't because you're using them wrong. This isn't because
                  you haven't found the "right" light yet.
                </p>

                <p className="font-bold" data-id="element-74">It's physics.</p>

                <p data-id="element-75">
                  When light travels through air from a source to your book, it
                  doesn't stay in a perfect beam. It expands. It scatters. It
                  illuminates everything in its path and beyond.
                </p>

                <h2 className="text-2xl md:text-3xl font-bold font-sans text-gray-900 mt-12 mb-6" data-id="element-76">
                  What's Really Happening In Your Relationship
                </h2>

                <p data-id="element-77">Here's the pattern you probably recognize:</p>

                <ul className="list-none pl-0 space-y-4 my-6" data-id="element-78">
                  <li className="flex gap-4" data-id="element-79">
                    <span className="font-bold text-gray-400 font-sans text-sm flex-shrink-0" data-id="element-80">
                      NIGHT 1
                    </span>
                    <span data-id="element-81">
                      Your partner shifts a little when you turn on the light.
                      Nothing dramatic. Just... noticeable.
                    </span>
                  </li>
                  <li className="flex gap-4" data-id="element-82">
                    <span className="font-bold text-gray-400 font-sans text-sm flex-shrink-0" data-id="element-83">
                      NIGHT 7
                    </span>
                    <span data-id="element-84">
                      There's a small sigh. You feel it more than hear it.
                    </span>
                  </li>
                  <li className="flex gap-4" data-id="element-85">
                    <span className="font-bold text-gray-400 font-sans text-sm flex-shrink-0" data-id="element-86">
                      NIGHT 20
                    </span>
                    <span data-id="element-87">
                      You're cutting chapters short. Closing the book earlier
                      than you wanted to. Telling yourself you're "just tired
                      anyway."
                    </span>
                  </li>
                  <li className="flex gap-4" data-id="element-88">
                    <span className="font-bold text-gray-400 font-sans text-sm flex-shrink-0" data-id="element-89">
                      NIGHT 40
                    </span>
                    <span data-id="element-90">
                      Your book sits on the nightstand. Unopened. Again.
                    </span>
                  </li>
                </ul>

                <p data-id="element-91">
                  This is what I call{' '}
                  <strong data-id="element-92">Silent Resentment Accumulation</strong>.
                </p>

                <p data-id="element-93">
                  Each time you choose to read, you make a tiny withdrawal from
                  your relationship bank account. Your partner loses sleep
                  quality. You gain guilt.
                </p>

                <p data-id="element-94">
                  Then the tension builds. A small comment here. A heavier sigh
                  there. Maybe they start going to bed earlier to "get ahead" of
                  your reading time.
                </p>

                <p data-id="element-95">
                  You notice. You feel it. So you start cutting chapters short.
                  Reading less. Feeling resentful that you have to.
                </p>

                <p data-id="element-96">
                  But here's the truth: You're not selfish. You're not
                  inconsiderate. You're not asking for too much.
                </p>

                <p className="font-bold text-lg" data-id="element-97">
                  You're just using tools that force you into an impossible
                  choice.
                </p>

                <SectionDivider data-id="element-98" />

                <h2 className="text-2xl md:text-3xl font-bold font-sans text-gray-900 mt-8 mb-6" data-id="element-99">
                  Then I Realized Something About My Phone Screen
                </h2>

                <div className="my-8" data-id="element-100">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/wPoTDn6dM16BJ2BbSrqWUc/Untitled_%281200_x_675_px%29_%281200_x_800_px%29_%281200_x_800_px%29_%281%29.png" alt="Person reading a book in bed with phone screen glowing in the dark" className="w-full rounded-lg shadow-2xl" data-id="element-101" />
                </div>

                <p data-id="element-102">
                  I was stuck in this impossible loop until one night, I was
                  lying in bed, scrolling my phone in the dark while my partner
                  slept next to me.
                </p>

                <p data-id="element-103">And I noticed something:</p>

                <p data-id="element-104">
                  When you look at your phone screen in a dark room, the screen
                  glows brightly... but the light doesn't spill all over your
                  face and the room, right?
                </p>

                <p data-id="element-105">
                  The screen is bright enough to read. Bright enough to see
                  every word clearly. But somehow, the light stays contained to
                  just the screen.
                </p>

                <p data-id="element-106">
                  My partner's face next to me? Dark. The wall behind me? Dark.
                  The ceiling? Dark. Only the screen glows.
                </p>

                <p className="font-bold" data-id="element-107">
                  That's because the light is contained IN the screen itself.
                </p>

                <p data-id="element-108">
                  It's not a lamp somewhere shooting light at the screen. The
                  screen itself is the light source, and the light is trapped
                  inside it.
                </p>

                <p data-id="element-109">
                  And I thought:{' '}
                  <em data-id="element-110">What if my book pages could glow like a phone screen?</em>
                </p>

                <p data-id="element-111">
                  What if there was a thin panel that sits on my book, where the
                  light is trapped inside the panel and only lights up my pages?
                </p>

                <p data-id="element-112">
                  That's what I needed. Not a lamp shooting light. But a glowing
                  surface. Not directed light. Not focused light. Not "carefully
                  aimed" light.
                </p>

                <p className="text-xl font-bold text-center my-6" data-id="element-113">
                  Contained light.
                </p>

                <p data-id="element-114">
                  That's when I found{' '}
                  <span className="text-[#28a745] font-bold" data-id="element-115">PageGlow</span>.
                </p>

                <h2 className="text-2xl md:text-3xl font-bold font-sans text-gray-900 mt-12 mb-6" data-id="element-116">
                  How PageGlow Actually Works (The Phone Screen Principle)
                </h2>

                <p data-id="element-117">
                  PageGlow is a thin optical panel—about as thick as a
                  smartphone—that sits directly on top of your book pages.
                </p>

                <p data-id="element-118">
                  Here's what makes it completely different from every light
                  you've tried:
                </p>

                <p data-id="element-119">
                  The light source isn't positioned away from your book. It's
                  built into the edges of the panel itself.
                </p>

                <p data-id="element-120">
                  Tiny LEDs inject light into the panel edges. That light
                  travels through the optical material—staying trapped inside
                  the panel—until it reaches your book pages and illuminates
                  them from directly underneath.
                </p>

                <div className="my-10 p-6 bg-gray-900 text-white rounded-lg shadow-xl" data-id="element-121">
                  <h3 className="text-xl font-bold mb-4 text-[#28a745] font-sans" data-id="element-122">
                    THE PHYSICS OF PAGEGLOW
                  </h3>
                  <ul className="space-y-3" data-id="element-123">
                    <li className="flex items-start gap-3" data-id="element-124">
                      <CheckCircle className="w-6 h-6 text-[#28a745] flex-shrink-0 mt-1" data-id="element-125" />
                      <span data-id="element-126">
                        <strong data-id="element-127">Contained Light:</strong> Just like your phone
                        screen, the light is contained within the panel. It
                        cannot escape beyond the edges.
                      </span>
                    </li>
                    <li className="flex items-start gap-3" data-id="element-128">
                      <CheckCircle className="w-6 h-6 text-[#28a745] flex-shrink-0 mt-1" data-id="element-129" />
                      <span data-id="element-130">
                        <strong data-id="element-131">Zero Spill:</strong> Your book pages glow. The
                        panel edges? That's where the light stops.
                      </span>
                    </li>
                    <li className="flex items-start gap-3" data-id="element-132">
                      <CheckCircle className="w-6 h-6 text-[#28a745] flex-shrink-0 mt-1" data-id="element-133" />
                      <span data-id="element-134">
                        <strong data-id="element-135">Perfect Darkness:</strong> One inch beyond your
                        book? Complete darkness. Your partner's pillow? Dark.
                      </span>
                    </li>
                  </ul>
                </div>

                <p data-id="element-136">
                  You're not carefully positioning it. You're not spending five
                  minutes adjusting angles. You're not trying to minimize spill.
                </p>

                <p className="font-bold" data-id="element-137">
                  The light physically cannot escape beyond the panel. It's
                  impossible.
                </p>

                <p data-id="element-138">The impossible choice disappears.</p>

                <h2 className="text-2xl md:text-3xl font-bold font-sans text-gray-900 mt-12 mb-6" data-id="element-139">
                  What This Actually Feels Like
                </h2>

                <div className="grid md:grid-cols-2 gap-8 my-8" data-id="element-140">
                  <div className="bg-red-50 p-6 rounded-lg border border-red-100" data-id="element-141">
                    <h3 className="font-bold text-red-800 text-lg mb-4 font-sans flex items-center gap-2" data-id="element-142">
                      <span className="text-2xl" data-id="element-143">❌</span> With Your Current
                      Light
                    </h3>
                    <p className="text-sm leading-relaxed text-gray-700" data-id="element-144">
                      It's 10 PM. You're reading with your clip-on light.
                      Fifteen minutes in, your partner puts down their phone and
                      rolls onto their side, facing away from you.
                      <br data-id="element-145" />
                      <br data-id="element-146" />
                      You can see the light from your clip-on hitting their
                      pillow. You adjust the neck. Try to angle it away.
                      <br data-id="element-147" />
                      <br data-id="element-148" />
                      Ten minutes later, they shift position. Pull the covers
                      higher. You feel it. That knot in your stomach.
                      <br data-id="element-149" />
                      <br data-id="element-150" />
                      You keep reading, but you're not fully in the story
                      anymore. Part of your attention is on them. Should you
                      turn it off? Are they annoyed?
                    </p>
                  </div>

                  <div className="bg-green-50 p-6 rounded-lg border border-green-100 relative overflow-hidden" data-id="element-151">
                    <div className="absolute top-0 right-0 bg-[#28a745] text-white text-xs font-bold px-3 py-1 rounded-bl-lg uppercase" data-id="element-152">
                      The Solution
                    </div>
                    <h3 className="font-bold text-green-800 text-lg mb-4 font-sans flex items-center gap-2" data-id="element-153">
                      <span className="text-2xl" data-id="element-154">✅</span> With PageGlow
                    </h3>
                    <p className="text-sm leading-relaxed text-gray-700" data-id="element-155">
                      It's 10 PM. You place PageGlow on the pages. Press the
                      button. Your pages glow with warm, comfortable light.
                      <br data-id="element-156" />
                      <br data-id="element-157" />
                      Your partner rolls onto their side. Their pillow stays
                      completely dark. You can see it. No light spill. Not even
                      a hint.
                      <br data-id="element-158" />
                      <br data-id="element-159" />
                      They don't shift. They don't pull the covers up. They
                      don't sigh. Because there's nothing to react to.
                      <br data-id="element-160" />
                      <br data-id="element-161" />
                      You keep reading. Ten minutes. Twenty. Thirty. They're
                      breathing deeply now. Fully asleep. You're completely
                      absorbed in the story. No knot in your stomach.
                    </p>
                  </div>
                </div>

                <p data-id="element-162">
                  You drift off to sleep feeling satisfied. Content. You got
                  your reading time. They got their sleep.
                </p>

                <p className="font-bold text-center text-xl my-6" data-id="element-163">
                  Nobody sacrificed anything.
                </p>

                <div className="my-12 text-center" data-id="element-164">
                  <CTAButton data-id="element-165">Check PageGlow Availability &raquo;</CTAButton>
                  <p className="text-sm text-gray-500 mt-3" data-id="element-166">
                    Limited stock available due to high demand
                  </p>
                </div>

                <h2 className="text-2xl md:text-3xl font-bold font-sans text-gray-900 mt-12 mb-6" data-id="element-167">
                  Thousands of Readers Are Finally Reading Guilt-Free
                </h2>

                <p data-id="element-168">
                  PageGlow isn't some experimental new technology. It's been
                  quietly transforming bedtime reading for thousands of book
                  lovers who were stuck in the same impossible choice you've
                  been facing.
                </p>

                <TestimonialBox quote="I haven't read this much in years. My husband told me last week he didn't even realize I'd been reading every night because he never noticed the light. That's when I knew this thing actually works." author="Jennifer M." location="Portland" image="https://randomuser.me/api/portraits/women/65.jpg" data-id="element-169" />

                <TestimonialBox quote="The first night I used it, I read for an hour and a half. My wife was sound asleep the entire time. I actually got emotional because I realized how much I'd been giving up just to avoid conflict." author="David R." location="Chicago" image="https://randomuser.me/api/portraits/men/32.jpg" data-id="element-170" />

                <TestimonialBox quote="It really does work like a glowing book page. I was skeptical about the phone screen comparison, but that's exactly what it feels like. My pages glow, nothing else does." author="Amanda T." location="Austin" image="https://randomuser.me/api/portraits/women/44.jpg" data-id="element-171" />

                <p data-id="element-172">
                  These aren't people with more understanding partners than you.
                  They're not reading smaller books or shorter chapters.
                </p>

                <p data-id="element-173">
                  They're using a light that actually solves the physics problem
                  instead of fighting against it.
                </p>

                <h2 className="text-2xl md:text-3xl font-bold font-sans text-gray-900 mt-12 mb-6" data-id="element-174">
                  "But Didn't Flat Panel Lights Fail Before?"
                </h2>

                <div className="my-8" data-id="element-175">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/g2vhVcTHG3FQ4i8vcx9kV9/Untitled_%281200_x_675_px%29_%281200_x_800_px%29_%281200_x_800_px%29_%283%29.png" alt="Modern PageGlow flat panel light illuminating an open book" className="w-full rounded-lg shadow-2xl" data-id="element-176" />
                </div>

                <p data-id="element-177">
                  If you've been reading for more than a decade, you might
                  remember the Lightwedge or other early flat panel lights from
                  the 2000s.
                </p>

                <p data-id="element-178">
                  And you might be thinking: "Those were terrible. Why would
                  this be any different?"
                </p>

                <p data-id="element-179">Fair question. Let me be completely honest with you:</p>

                <p data-id="element-180">
                  Those early flat panel lights used the same basic
                  principle—light contained in a flat panel. The physics was
                  always right. But the execution was awful.
                </p>

                <p data-id="element-181">
                  The acrylic scratched within weeks. Every scratch caught the
                  light, creating a hazy, unusable surface. The glare reflected
                  back into your eyes. They were bulky, heavy, and awkward to
                  hold.
                </p>

                <p className="font-bold" data-id="element-182">So what's different now?</p>

                <p data-id="element-183">Twenty years of materials science advancement.</p>

                <p data-id="element-184">
                  PageGlow uses optical-grade, scratch-resistant materials—the
                  same quality used in premium camera lenses. You can use it
                  every night for years. The surface stays crystal clear. No
                  hazing. No scratching.
                </p>

                <p data-id="element-185">
                  Anti-glare coating technology has completely eliminated the
                  reflection problem. Light goes through to your page, not back
                  at your eyes.
                </p>

                <p data-id="element-186">
                  Judging PageGlow by a 2005 Lightwedge is like judging a 2025
                  smartphone by a 2005 flip phone. Same category. Completely
                  different execution.
                </p>

                <h2 className="text-2xl md:text-3xl font-bold font-sans text-gray-900 mt-12 mb-6" data-id="element-187">
                  What Makes PageGlow Different From Everything Else
                </h2>

                <div className="grid md:grid-cols-2 gap-6 my-8" data-id="element-188">
                  {[{
                  title: 'Thin and lightweight',
                  desc: 'Barely thicker than a smartphone. Slip it in your bag, take it anywhere.'
                }, {
                  title: 'Rechargeable and wireless',
                  desc: 'No cords trailing across your bed. USB-C charging.'
                }, {
                  title: 'Adjustable brightness',
                  desc: 'Simple touch controls let you adjust it instantly without leaving the page.'
                }, {
                  title: 'Works with any book size',
                  desc: "Hardcovers, paperbacks, trade paperbacks—doesn't matter."
                }, {
                  title: 'Warm light option',
                  desc: "Designed to be easy on your eyes and won't disrupt your sleep like blue light."
                }, {
                  title: 'Actually durable',
                  desc: 'Scratch-resistant surface means you can use it every night.'
                }].map((feature, i) => <div key={i} className="flex gap-3" data-id="element-189">
                      <div className="mt-1 bg-green-100 rounded-full p-1 h-fit" data-id="element-190">
                        <CheckCircle className="w-5 h-5 text-[#28a745]" data-id="element-191" />
                      </div>
                      <div data-id="element-192">
                        <h4 className="font-bold font-sans text-gray-900" data-id="element-193">
                          {feature.title}
                        </h4>
                        <p className="text-sm text-gray-600 leading-normal" data-id="element-194">
                          {feature.desc}
                        </p>
                      </div>
                    </div>)}
                </div>

                <div className="bg-[#f8f9fa] p-8 rounded-xl border border-gray-200 shadow-sm my-12" data-id="element-195">
                  <h2 className="text-2xl md:text-3xl font-bold font-sans text-gray-900 mb-6 text-center" data-id="element-196">
                    Here's Your Opportunity To End The Impossible Choice
                  </h2>

                  <p data-id="element-197">
                    For the first time, you can read as long as you want,
                    whenever you want, without guilt.
                  </p>

                  <p data-id="element-198">
                    Not because your partner is being more tolerant. Not because
                    you're sacrificing your comfort. But because the physics of
                    the light itself eliminates the conflict.
                  </p>

                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 my-6" data-id="element-199">
                    <p className="font-bold text-yellow-800 m-0" data-id="element-200">
                      🔥 Special Offer: Readers can save 32% on PageGlow today.
                    </p>
                  </div>

                  <p data-id="element-201">
                    This isn't the regular price. This is a limited opportunity
                    for people who've been stuck in the same impossible choice.
                  </p>

                  <div className="flex flex-col items-center gap-4 my-8" data-id="element-202">
                    <div className="text-2xl font-bold text-[#28a745]" data-id="element-203">
                      Save 32% Today - Limited Time Offer
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-lg border border-gray-200 mb-8" data-id="element-204">
                    <div className="flex items-center gap-3 mb-4" data-id="element-205">
                      <ShieldCheck className="w-8 h-8 text-[#28a745]" data-id="element-206" />
                      <h3 className="font-bold text-xl font-sans m-0" data-id="element-207">
                        60-Day Money-Back Guarantee
                      </h3>
                    </div>
                    <p className="text-sm m-0" data-id="element-208">
                      Use it every night for two months. If it doesn't eliminate
                      the impossible choice—if you still feel guilty reading at
                      night, if light still spills onto your partner's
                      pillow—send it back.{' '}
                      <strong data-id="element-209">Full refund. No questions asked.</strong>
                    </p>
                  </div>

                  <div className="text-center" data-id="element-210">
                    <CTAButton className="w-full text-xl py-5 shadow-xl animate-pulse" data-id="element-211">
                      GET PAGEGLOW - SAVE 32% TODAY
                    </CTAButton>
                    <div className="flex justify-center gap-4 mt-4 text-xs text-gray-500" data-id="element-212">
                      <span className="flex items-center gap-1" data-id="element-213">
                        <ThumbsUp className="w-3 h-3" data-id="element-214" /> 98% Satisfaction Rating
                      </span>
                      <span className="flex items-center gap-1" data-id="element-215">
                        <Clock className="w-3 h-3" data-id="element-216" /> Ships within 24 hours
                      </span>
                    </div>
                  </div>
                </div>

                <p data-id="element-217">Tonight, you can read as long as you want.</p>

                <p data-id="element-218">Click the button below to get PageGlow with 32% savings.</p>

                <p className="italic text-gray-600 text-center" data-id="element-219">
                  Try it for 60 days, completely risk-free. Tonight, you'll open
                  your book without hesitation. Your pages will glow. Your
                  partner will stay asleep. And for the first time in a long
                  time, you'll read for as long as you want.
                </p>
              </div>
            </article>

            <SectionDivider data-id="element-220" />

            {/* Disclaimer Section */}
            <div className="bg-gray-100 p-6 rounded text-xs text-gray-500 leading-relaxed mb-12" data-id="element-221">
              <p className="mb-2" data-id="element-222">
                This is an advertisement and not an actual news article, blog,
                or consumer protection update. Results may vary. Individual
                experiences with products may differ.
              </p>
              <p data-id="element-223">
                <strong data-id="element-224">Disclosure:</strong> This website is an independent
                professional comparison and review site supported by referral
                fees from the sites and products featured.
              </p>
            </div>
          </div>

          {/* Sidebar - Recommended Product Box */}
          <aside className="lg:w-80 flex-shrink-0" data-id="element-225">
            <div className="sticky top-24 border-2 border-gray-300 rounded-lg p-6 bg-white shadow-lg" data-id="element-226">
              <h3 className="text-2xl font-bold text-center mb-4 font-sans text-gray-900" data-id="element-227">
                Recommended:
              </h3>

              <div className="flex justify-center mb-3" data-id="element-228">
                {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-6 h-6 text-yellow-400 fill-current" data-id="element-229" />)}
              </div>

              <p className="text-center text-sm text-gray-600 mb-4" data-id="element-230">
                <span className="font-bold text-gray-900" data-id="element-231">4.68</span> | 100+
                Verified Reviews
              </p>

              <h4 className="text-xl font-bold text-center mb-6 text-gray-900" data-id="element-232">
                PageGlow Reading Light
              </h4>

              <ul className="space-y-3 mb-6" data-id="element-233">
                {['Zero light spill - partner stays asleep', 'Thin, lightweight, portable design', 'Rechargeable - hours of reading per charge', 'Works with any book size', 'Warm, eye-friendly illumination', 'Scratch-resistant optical surface', '60-Day Money-Back Guarantee*'].map((feature, i) => <li key={i} className="flex items-start gap-2 text-sm text-gray-700" data-id="element-234">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-235" />
                    <span data-id="element-236">{feature}</span>
                  </li>)}
              </ul>

              <div className="mb-6" data-id="element-237">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/xrxjnBE4zbVTK2ZE61VfKX/Screenshot_2025-10-31_184122_ac64def2-ccdd-44b5-a624-529e37c9873d.webp" alt="PageGlow reading light illuminating a book in cozy setting" className="w-full h-auto rounded-lg" data-id="element-238" />
              </div>

              <a href="https://flashgizmo.com/products/pageglow%E2%84%A2" target="_blank" rel="noopener noreferrer" className="block w-full bg-[#28a745] hover:bg-[#218838] text-white font-bold py-3 px-6 rounded-full shadow-lg transform transition hover:-translate-y-0.5 text-base uppercase tracking-wide text-center" data-id="element-239">
                Check Availability
              </a>
            </div>
          </aside>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 text-sm" data-id="element-240">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-241">
          <div className="flex flex-wrap justify-center gap-6 mb-8" data-id="element-242">
            <a href="#" className="hover:text-white transition" data-id="element-243">
              About Us
            </a>
            <a href="#" className="hover:text-white transition" data-id="element-244">
              Terms of Use
            </a>
            <a href="#" className="hover:text-white transition" data-id="element-245">
              Privacy Policy
            </a>
            <a href="#" className="hover:text-white transition" data-id="element-246">
              Contact
            </a>
          </div>
          <p className="mb-4" data-id="element-247">
            Trademarks utilized on our website belong to their respective owners
            and no implied or expressed endorsement of our website or services
            is intended. Through in-depth research and experienced editors we
            provide feedback about products and services. We are independently
            owned, and opinions expressed here are our own.
          </p>
          <p data-id="element-248">&copy; 2025 Lifestyle Insights Journal. All Rights Reserved.</p>
        </div>
      </footer>
    </div>;
}